import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useA11y } from '../context/AccessibilityContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Check, ChevronRight, Tv, Cast, Dna, Users, Search, X, Sparkles } from 'lucide-react';
import { PLAY_BUTTON_GRADIENT } from '../lib/theme';
import { launchCard } from '../lib/launch';
import EpisodePicker from '../components/EpisodePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'Max',          color: '#B535F6' },
  max:            { name: 'Max',          color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  prime_video:    { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#bbbbbb' },
  apple_tv_plus:  { name: 'Apple TV+',    color: '#bbbbbb' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

function svc(id) {
  return SERVICE_META[id] || { name: id || 'Unknown', color: '#666' };
}

function epLabel(s, e) {
  if (s == null && e == null) return '';
  if (s == null) return `E${e}`;
  return `S${s}E${e}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

const HUB_MOODS = [
  { id: 'stressed',      emoji: '😤', label: 'Stressed' },
  { id: 'happy',         emoji: '😄', label: 'Happy' },
  { id: 'bored',         emoji: '😑', label: 'Bored' },
  { id: 'adventurous',   emoji: '🤠', label: 'Adventure' },
  { id: 'romantic',      emoji: '🥰', label: 'Romantic' },
  { id: 'sad',           emoji: '😢', label: 'Sad' },
  { id: 'nostalgic',     emoji: '🕰️', label: 'Nostalgic' },
  { id: 'escapist',      emoji: '🚀', label: 'Escapist' },
  { id: 'contemplative', emoji: '🤔', label: 'Think' },
  { id: 'energetic',     emoji: '⚡', label: 'Energetic' },
];

export default function Hub() {
  const { user, getToken } = useAuth();
  const [hub, setHub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing]   = useState(null);
  const [managing, setManaging]     = useState(null);
  const [positioning, setPositioning] = useState(null);
  const [dna, setDna]               = useState(null);
  const [qmates, setQmates]         = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodResults, setMoodResults]   = useState([]);
  const [moodResultIndex, setMoodResultIndex] = useState(0);
  const [moodLoading, setMoodLoading]   = useState(false);
  const [trending, setTrending]         = useState([]);
  const [watchNext, setWatchNext]       = useState([]);
  const [watchNextIdx, setWatchNextIdx] = useState(0);
  const [dropAlerts, setDropAlerts]     = useState(new Set());
  const [episodeBadges, setEpisodeBadges] = useState({});
  const [togglingAlert, setTogglingAlert] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [searchQuery, setSearchQuery]   = useState(() => sessionStorage.getItem('hub_search') || '');
  const [searchResults, setSearchResults] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searching, setSearching]       = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [correctingDetection, setCorrectingDetection] = useState(null);
  const [fixingService, setFixingService] = useState(null);
  const [remindingCard, setRemindingCard] = useState(null);
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderDate, setReminderDate]   = useState('');
  const [reminderSaving, setReminderSaving] = useState(false);
  const pullStartY = useRef(0);
  const pullDelta  = useRef(0);

  const [loadError, setLoadError] = useState(false);

  // Cold app-start in the Android WebView: Clerk's session needs a moment
  // to rehydrate from persisted storage after a full process kill, and
  // getToken() can transiently return null or a not-yet-valid token during
  // that window -- a race, not a real auth failure (same class of bug
  // already fixed in AppShell.js's isAndroid check and Settings.js's
  // permission detection). A single failed attempt with no retry made a
  // cold reopen look like the hub had emptied itself. Retry a few times
  // with backoff before surfacing a real error.
  const load = useCallback(async (attempt = 0) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('no token yet');
      const headers = { Authorization: `Bearer ${token}` };
      const [hubRes, dnaRes, qmatesRes, trendingRes, alertsRes, watchNextRes] = await Promise.all([
        axios.get(`${API}/hub`,           { headers, timeout: 30000 }),
        axios.get(`${API}/watch-dna`,     { headers, timeout: 15000 }).catch(() => ({ data: null })),
        axios.get(`${API}/qmates`,        { headers, timeout: 15000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/tmdb/trending`, { headers, timeout: 10000 }).catch(() => ({ data: { results: [] } })),
        axios.get(`${API}/drop-alerts`,   { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/watch-next`,    { headers, timeout: 12000 }).catch(() => ({ data: { picks: [] } })),
      ]);
      setHub(hubRes.data);
      setLoadError(false);
      // "When's the next episode?" badges -- same /api/tmdb/next-episode
      // Watchlist.js already uses, just also surfaced on the Hub (Continue
      // Watching / Up Next / Still Watching), which previously showed no
      // air-date info at all outside the Watchlist and per-show detail page.
      // Fire-and-forget: badges are supplementary, shouldn't block the hub.
      (async () => {
        const candidates = [
          ...(hubRes.data?.resume ? [hubRes.data.resume] : []),
          ...(hubRes.data?.up_next || []),
          ...(hubRes.data?.stale || []),
        ].filter(c => c.tmdb_id && c.media_type !== 'movie');
        const seen = new Set();
        const unique = candidates.filter(c => {
          if (seen.has(c.show_key)) return false;
          seen.add(c.show_key);
          return true;
        });
        if (unique.length === 0) return;
        const badges = {};
        await Promise.all(unique.map(async (c) => {
          try {
            const res = await axios.get(`${API}/tmdb/next-episode/${c.tmdb_id}`, { headers });
            if (res.data.next_episode) badges[c.show_key] = res.data.next_episode;
          } catch {}
        }));
        setEpisodeBadges(badges);
      })();
      setDna(dnaRes.data);
      setQmates((qmatesRes.data || []).filter(q => q.status === 'accepted'));
      setTrending(trendingRes.data?.results || []);
      setWatchNext(watchNextRes.data?.picks || []);
      setWatchNextIdx(0);
      setDropAlerts(new Set((alertsRes.data || []).map(a => a.show_key)));
      const watchingCards = (hubRes.data?.up_next || []).filter(c => c.tmdb_id).slice(0, 2);
      if (watchingCards.length > 0) {
        const recResults = await Promise.all(
          watchingCards.map(async c => {
            try {
              const res = await axios.get(`${API}/tmdb/recommendations/${c.tmdb_id}`, {
                params: { media_type: c.media_type || 'tv' }, headers, timeout: 8000,
              });
              const results = (res.data?.results || []).filter(r => r.poster_url);
              return results.length > 0 ? { forTitle: c.title, tmdbId: c.tmdb_id, results: results.slice(0, 8) } : null;
            } catch { return null; }
          })
        );
        setRecommendations(recResults.filter(Boolean));
      }
      setLoading(false);
    } catch (err) {
      if (attempt < 3) {
        setTimeout(() => load(attempt + 1), 400 * (attempt + 1));
        return;
      }
      console.error('Hub load failed after retries:', err);
      setLoadError(true);
      setLoading(false);
      toast.error('Could not load your hub');
    }
  }, [getToken]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Refresh when the user switches back to this tab (e.g., after watching on Netflix)
  useEffect(() => {
    const onVisible = () => { if (!document.hidden && user) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, load]);

  const fetchMood = useCallback(async (moodId) => {
    setSelectedMood(moodId);
    setMoodLoading(true);
    setMoodResults([]);
    setMoodResultIndex(0);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/mood/queue`, { mood: moodId },
        { headers: { Authorization: `Bearer ${token}` } });
      setMoodResults((res.data.results || []).filter((r) => !r.in_progress));
    } catch { toast.error('Could not load mood suggestions'); }
    finally { setMoodLoading(false); }
  }, [getToken]);

  const saveReminder = useCallback(async (card) => {
    if (!reminderTitle || !reminderDate) { toast.error('Title and date are required'); return; }
    setReminderSaving(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/reminders`, { title: reminderTitle, reminder_date: reminderDate },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reminder set!');
      setRemindingCard(null); setReminderTitle(''); setReminderDate('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save reminder'); }
    finally { setReminderSaving(false); }
  }, [getToken, reminderTitle, reminderDate]);

  const advance = useCallback(async (card) => {
    setAdvancing(card.show_key);
    try {
      const token = await getToken();
      await axios.post(`${API}/track/advance`, { show_key: card.show_key, episodes: 1 },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked ${card.title} watched`);
      setHub(prev => ({ ...prev, resume: prev?.resume?.show_key === card.show_key ? null : prev?.resume }));
      await load();
    } catch { toast.error('Could not update'); }
    finally { setAdvancing(null); }
  }, [getToken, load]);

  const setStatus = useCallback(async (card, status) => {
    setManaging(null);
    try {
      const token = await getToken();
      await axios.post(`${API}/track/set`, { show_key: card.show_key, status },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(status === 'finished' ? 'Marked as watched' : 'Updated');
      setHub(prev => ({ ...prev, resume: prev?.resume?.show_key === card.show_key ? null : prev?.resume }));
      await load();
    } catch { toast.error('Could not update'); }
  }, [getToken, load]);

  const fixService = useCallback(async (card, serviceId) => {
    try {
      const token = await getToken();
      await axios.post(`${API}/track/set`, { show_key: card.show_key, service: serviceId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Service updated');
      setFixingService(null);
      await load();
    } catch { toast.error('Could not update service'); }
  }, [getToken, load]);

  const removeFromHub = useCallback(async (card) => {
    setManaging(null);
    try {
      const token = await getToken();
      await axios.post(`${API}/track/remove`, { show_key: card.show_key, tmdb_id: String(card.tmdb_id || '') },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Removed ${card.title}`);
      await load();
    } catch { toast.error('Could not remove'); }
  }, [getToken, load]);

  // Self-serve rebuild: watchlist shows added before shows started
  // dual-writing into viewing_state only ever exist in the `shows`
  // collection, which the Hub never reads. This replays them through the
  // same reconciliation engine live signals use -- no admin secret needed.
  const [rebuilding, setRebuilding] = useState(false);
  const rebuildHub = useCallback(async () => {
    setRebuilding(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/hub/rebuild`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Rebuilt: ${res.data.viewing_state_rows} show${res.data.viewing_state_rows === 1 ? '' : 's'} in your hub now`);
      await load();
    } catch { toast.error('Could not rebuild hub'); }
    finally { setRebuilding(false); }
  }, [getToken, load]);

  const openPositioner = useCallback((card) => {
    setManaging(null);
    setPositioning({ show_key: card.show_key, season: card.current_season ?? 1, episode: card.current_episode ?? 1 });
  }, []);

  const savePosition = useCallback(async () => {
    if (!positioning) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/track/set`,
        { show_key: positioning.show_key, season: positioning.season, episode: positioning.episode, status: 'watching' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Updated to S${positioning.season}E${positioning.episode}`);
      setPositioning(null);
      await load();
    } catch { toast.error('Could not update'); }
  }, [positioning, getToken, load]);

  const flagDetection = useCallback(async (card, correct) => {
    setManaging(null);
    setCorrectingDetection(card.show_key);
    try {
      const token = await getToken();
      await axios.post(`${API}/shows/detection-feedback`,
        { show_id: card.show_key, tmdb_id: String(card.tmdb_id || ''), correct, detected_title: card.title || '' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(correct ? 'Thanks for the confirmation!' : 'Got it — we won\'t count that detection');
      if (!correct) await load();
    } catch { toast.error('Could not record feedback'); }
    setCorrectingDetection(null);
  }, [getToken, load]);

  const undoInference = useCallback(async (card) => {
    setManaging(null);
    if (!card.inferred_session_id) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/inference/session/${card.inferred_session_id}/undo`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Removed — thanks for the correction');
      await load();
    } catch { toast.error('Could not undo'); }
  }, [getToken, load]);

  const openReminderForm = useCallback((card) => {
    setManaging(null);
    setRemindingCard(card.show_key);
    setReminderTitle(card.title || '');
    setReminderDate('');
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/tmdb/search`, {
          params: { q: searchQuery }, headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
        });
        setSearchResults(res.data?.results || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, getToken]);

  const toggleDropAlert = useCallback(async (card) => {
    const key = card.show_key;
    setTogglingAlert(key);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      if (dropAlerts.has(key)) {
        await axios.delete(`${API}/drop-alerts/${encodeURIComponent(key)}`, { headers });
        setDropAlerts(prev => { const s = new Set(prev); s.delete(key); return s; });
        toast.success('Drop alert removed');
      } else {
        await axios.post(`${API}/drop-alerts`, { show_key: key, title: card.title, tmdb_id: card.tmdb_id || '' }, { headers });
        setDropAlerts(prev => new Set([...prev, key]));
        toast.success('Drop alert set!');
      }
    } catch { toast.error('Could not update drop alert'); }
    finally { setTogglingAlert(null); setManaging(null); }
  }, [getToken, dropAlerts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 320 }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
             style={{ borderColor: '#4A9EFF', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // Distinct from "genuinely empty" below -- this is "couldn't even fetch,"
  // after retries were already exhausted in load(). Never show the rebuild/
  // import empty-state UI here: nothing was actually confirmed empty.
  if (loadError && !hub) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-center px-6" style={{ minHeight: 320 }}>
        <p className="text-sm text-neutral-400">Couldn't load your hub — check your connection.</p>
        <button
          onClick={() => { setLoading(true); setLoadError(false); load(); }}
          className="px-5 py-2 rounded-full text-sm font-semibold text-white"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}>
          Retry
        </button>
      </div>
    );
  }

  const empty = hub && !hub.resume && (hub.continue_watching?.length || 0) === 0 && (hub.watchlist?.length || 0) === 0 && (hub.finished?.length || 0) === 0 && (hub.stale?.length || 0) === 0;
  const firstName = user?.firstName || '';
  const currentMoodResult = moodResults[moodResultIndex] || null;
  const resumeCard = hub?.resume || hub?.up_next?.[0] || null;

  const handleTouchStart = (e) => { if (window.scrollY === 0) pullStartY.current = e.touches[0].clientY; };
  const handleTouchMove  = (e) => { if (pullStartY.current === 0) return; pullDelta.current = e.touches[0].clientY - pullStartY.current; };
  const handleTouchEnd   = async () => {
    if (pullDelta.current > 80 && !refreshing) { setRefreshing(true); await load(); setRefreshing(false); }
    pullStartY.current = 0; pullDelta.current = 0;
  };

  // Client-reported half of backend/recommendation_events.py's feedback
  // ledger -- GET /api/watch-next logs "shown" server-side (it's the only
  // one who knows what was actually returned); this fills in "clicked"
  // when the user acts on the Qude'd Up pick. Fire-and-forget: never
  // blocks the Link's own navigation.
  const logWatchNextClick = async (pick) => {
    try {
      const token = await getToken();
      await axios.post(
        `${API}/recommendations/event`,
        { surface: 'watch_next', event_type: 'clicked', tmdb_id: String(pick.tmdb_id), title: pick.title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // best-effort only
    }
  };

  // Filtered by show_key against resumeCard explicitly, not a blind
  // slice(1) -- resumeCard sometimes falls back to up_next[0] (see above)
  // rather than hub.resume, and a blind slice only ever dropped the literal
  // first element, not necessarily whichever one is actually shown as the
  // hero right now.
  const upNextCards = hub?.up_next
    ? hub.up_next
        .filter(c => c.title && !c.title.includes('Button'))
        .filter(c => !resumeCard || c.show_key !== resumeCard.show_key)
    : [];
  const staleCards = (hub?.stale || [])
    .filter(c => !resumeCard || c.show_key !== resumeCard.show_key);

  return (
    <div className="max-w-5xl mx-auto pb-20"
      onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes hubrise { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: none; } }
        .hub-rise { animation: hubrise .5s ease both; }
      `}</style>

      {refreshing && (
        <div className="flex items-center justify-center py-3 gap-2 text-xs text-neutral-500">
          <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          Refreshing…
        </div>
      )}

      {/* Greeting — env(safe-area-inset-top) pushes below status bar/notch on Android 15 edge-to-edge */}
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', paddingBottom: '8px' }}>
        <h1 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
          Good {getGreeting()}{firstName ? `, ${firstName}` : ''}
        </h1>
      </div>

      {/* SEARCH */}
      <div className="relative mb-2">
        <div className="relative">
          {searching
            ? <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            : <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600 pointer-events-none" />}
          <input type="text" placeholder="Search shows & movies…" value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); sessionStorage.setItem('hub_search', e.target.value); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            className="w-full pl-10 pr-9 py-3 rounded-2xl bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-violet-500/40 transition-colors" />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); setSearchResults([]); sessionStorage.removeItem('hub_search'); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-300 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchFocused && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 z-30 rounded-2xl border border-white/10 bg-[#111] shadow-2xl overflow-hidden">
            {searchResults.map(item => (
              <Link key={item.tmdb_id} to={`/show/${item.tmdb_id}`} state={{ media_type: item.media_type || 'tv' }}
                onClick={() => { setSearchQuery(''); setSearchResults([]); sessionStorage.removeItem('hub_search'); }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 no-underline">
                {item.poster_url
                  ? <img src={item.poster_url} alt={item.title} className="w-8 h-12 rounded-lg object-cover flex-shrink-0" />
                  : <div className="w-8 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0"><Tv className="w-4 h-4 text-neutral-600" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{item.title}</p>
                  <p className="text-xs text-neutral-500">
                    {item.media_type === 'movie' ? '🎬 Movie' : '📺 TV'}
                    {item.year ? ` · ${item.year}` : ''}
                    {item.rating > 0 ? ` · ⭐ ${item.rating}` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* NOW ON TV */}
      {hub?.now_playing && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-2xl border border-green-500/20 bg-green-500/5">
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{hub.now_playing.title}</p>
            <p className="text-xs text-green-400/70">
              Now on {hub.now_playing.device_name || hub.now_playing.device_type}
              {hub.now_playing.season && hub.now_playing.episode ? ` · S${hub.now_playing.season}E${hub.now_playing.episode}` : ''}
            </p>
          </div>
          <Cast className="w-4 h-4 text-green-400/60 flex-shrink-0" />
        </div>
      )}

      {/* EMPTY STATE */}
      {empty && (
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-12 text-center hub-rise">
          <Tv className="w-12 h-12 mx-auto mb-4" style={{ color: '#333' }} />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Your hub is empty</h2>
          <p className="text-neutral-500 text-sm mb-6 max-w-sm mx-auto">Add shows to your watchlist to fill this in.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a href="/watchlist"><button className="px-5 py-2.5 rounded-full text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}>Browse shows</button></a>
            <button onClick={rebuildHub} disabled={rebuilding}
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-neutral-300 border border-white/10 disabled:opacity-50">
              {rebuilding ? 'Rebuilding…' : 'Already have shows in your watchlist?'}
            </button>
          </div>
        </div>
      )}

      {/* HERO RESUME CARD — Continue Watching leads the Hub. */}
      {resumeCard && (
        <>
          <div className="flex items-center gap-1.5 mb-2">
            <Play className="w-3 h-3 text-violet-400" />
            <span className="text-[11px] font-black tracking-widest text-neutral-500 uppercase">Continue Watching</span>
          </div>
          <ResumeCard
            card={resumeCard}
            episodeBadge={episodeBadges[resumeCard.show_key]}
            onPlay={() => launchCard(resumeCard, getToken)}
            advancing={advancing === resumeCard.show_key}
            onAdvance={() => advance(resumeCard)}
            menuOpen={managing === resumeCard.show_key}
            onMenuToggle={() => setManaging(managing === resumeCard.show_key ? null : resumeCard.show_key)}
            onFinished={() => setStatus(resumeCard, 'finished')}
            onRemove={() => removeFromHub(resumeCard)}
            positioningState={positioning?.show_key === resumeCard.show_key ? positioning : null}
            onSetEpisode={() => openPositioner(resumeCard)}
            onPositionChange={setPositioning}
            onPositionSave={savePosition}
            onPositionCancel={() => setPositioning(null)}
            onSetReminder={() => openReminderForm(resumeCard)}
            onDropAlert={() => toggleDropAlert(resumeCard)}
            dropAlertActive={dropAlerts.has(resumeCard.show_key)}
            onFlagCorrect={() => flagDetection(resumeCard, true)}
            onFlagWrong={() => flagDetection(resumeCard, false)}
            correcting={correctingDetection === resumeCard.show_key}
            onFixService={() => { setManaging(null); setFixingService('resume'); }}
            onUndoInference={() => undoInference(resumeCard)}
          />
          {fixingService === 'resume' && (
            <ServicePicker onSave={(svcId) => fixService(resumeCard, svcId)} onCancel={() => setFixingService(null)} />
          )}
          {remindingCard === resumeCard.show_key && (
            <ReminderForm
              title={reminderTitle}
              date={reminderDate}
              saving={reminderSaving}
              onTitleChange={setReminderTitle}
              onDateChange={setReminderDate}
              onSave={() => saveReminder(resumeCard)}
              onCancel={() => setRemindingCard(null)}
            />
          )}
        </>
      )}

      {/* QUDE'D UP — the focal point of the Hub. One confident pick (your
          currently-watching / watchlist first, discoveries after), big and up
          top. "Try something else" walks the list. */}
      {(() => {
        const resumeTitle = (resumeCard?.title || '').trim().toLowerCase();
        const wnList = watchNext.filter(p => {
          // GET /api/watch-next tags a "continue watching" pick with this
          // exact reason prefix specifically so it leads the list -- that's
          // right for the list itself, but it's already the show shown
          // above as the hero/resume card, so it shouldn't be offered here
          // a second time.
          if ((p.reason || '').toLowerCase().startsWith('continue watching')) return false;
          if (!resumeCard) return true;
          // tmdb_id is the reliable match, but a detected/auto-tracked show
          // can have a null tmdb_id (never resolved against TMDB) -- falling
          // back to title match there instead of skipping the check
          // entirely, which previously let an unresolved resume show sail
          // through this filter and appear a second time as this pick too.
          if (resumeCard.tmdb_id) return String(p.tmdb_id) !== String(resumeCard.tmdb_id);
          return (p.title || '').trim().toLowerCase() !== resumeTitle;
        });
        if (wnList.length === 0) return null;
        const pick = wnList[watchNextIdx % wnList.length];
        const pickSvc = pick.service ? svc(pick.service) : null;
        return (
          <div className="mb-8 hub-rise">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-violet-400" />
              <span className="text-[11px] font-black tracking-widest text-neutral-400 uppercase">Qude'd Up · Watch Next</span>
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-violet-500/20 bg-gradient-to-br from-violet-600/15 via-[#0A0A0A] to-[#4A9EFF]/10">
              <div className="flex flex-col sm:flex-row gap-5 p-5 sm:p-6 items-center sm:items-stretch">
                <Link to={`/show/${pick.tmdb_id}`} state={{ media_type: pick.media_type || 'tv' }} onClick={() => logWatchNextClick(pick)} className="flex-shrink-0 no-underline">
                  {(pick.poster_url || pick.image_url)
                    ? <img src={pick.poster_url || pick.image_url} alt={pick.title} className="rounded-2xl object-cover shadow-2xl" style={{ width: 160, height: 240 }} />
                    : <div className="rounded-2xl bg-white/5 flex items-center justify-center" style={{ width: 160, height: 240 }}><Tv className="w-10 h-10 text-neutral-600" /></div>}
                </Link>
                <div className="flex-1 min-w-0 flex flex-col justify-center text-center sm:text-left">
                  <div className="flex items-center gap-2 justify-center sm:justify-start mb-1 flex-wrap">
                    {pickSvc && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0"
                            style={{ background: `${pickSvc.color}28`, border: `1px solid ${pickSvc.color}50`, color: pickSvc.color }}>
                        {pickSvc.name}
                      </span>
                    )}
                    <p className="text-xs uppercase tracking-widest text-violet-300">{pick.reason || 'Picked for you'}</p>
                  </div>
                  <Link to={`/show/${pick.tmdb_id}`} state={{ media_type: pick.media_type || 'tv' }} onClick={() => logWatchNextClick(pick)} className="no-underline">
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-1 leading-tight" style={{ fontFamily: 'Outfit' }}>{pick.title}</h2>
                  </Link>
                  {pick.rating > 0 && <p className="text-sm text-neutral-400 mb-5">⭐ {pick.rating}</p>}
                  <div className="flex gap-3 flex-wrap justify-center sm:justify-start mt-auto">
                    <Link to={`/show/${pick.tmdb_id}`} state={{ media_type: pick.media_type || 'tv' }} onClick={() => logWatchNextClick(pick)} className="no-underline">
                      <button className="px-6 py-3 rounded-full text-base font-bold text-white shadow-lg" style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}>▶ Watch this</button>
                    </Link>
                    <button
                      onClick={() => setWatchNextIdx(i => i + 1)}
                      className="px-6 py-3 rounded-full text-base font-semibold text-neutral-200 border border-white/15 hover:border-white/35 hover:bg-white/5 transition-all">
                      Try something else
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Watch DNA */}
      {dna && (
        <Link to="/watch-dna" className="block mb-2 hub-rise">
          <div className="rounded-2xl p-4 flex items-center gap-4 border border-white/5 hover:border-violet-500/30 transition-all"
               style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(74,158,255,0.06) 100%)' }}>
            <div className="relative flex-shrink-0 w-12 h-12">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                <circle cx="24" cy="24" r="20" fill="none" stroke="url(#dnaGrad)" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - (dna.completion_rate ?? 0) / 100)}`} />
                <defs>
                  <linearGradient id="dnaGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#4A9EFF" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <Dna className="w-4 h-4" style={{ color: '#a78bfa' }} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-[0.06em] uppercase text-neutral-500 mb-0.5">Watch DNA</p>
              <p className="text-sm font-bold text-white truncate">{dna.dna_label || 'Your Watch Personality'}</p>
              <p className="text-xs text-neutral-500">
                {dna.total_shows != null ? `${dna.total_shows} shows` : ''}
                {dna.top_service ? ` · ${svc(dna.top_service).name}` : ''}
                {dna.completion_rate != null ? ` · ${Math.round(dna.completion_rate)}% finish rate` : ''}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-600 flex-shrink-0" />
          </div>
        </Link>
      )}

      {/* UP NEXT */}
      {upNextCards.length > 0 && (
        <Section title="Keep watching">
          <div className="flex flex-col gap-2">
            {upNextCards.map((c) => {
              const s = svc(c.service || c.service_id);
              const ep = (c.next_season != null || c.next_episode != null)
                ? epLabel(c.next_season, c.next_episode)
                : epLabel(c.current_season, c.current_episode);
              return (
                <div key={c.show_key}>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-2xl border border-white/8 bg-[#111] w-full" style={{ minHeight: 140 }}>
                    <div className="relative flex-shrink-0 rounded-xl overflow-hidden" style={{ width: 88, height: 124 }}>
                      {c.image_url || c.poster_url
                        ? <img src={c.image_url || c.poster_url} alt={c.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg,${s.color}33,#0a0a0a)` }}><Tv className="w-4 h-4 text-neutral-600" /></div>}
                      <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-black" style={{ background: s.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate leading-tight" style={{ fontFamily: 'Outfit' }}>{c.title}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{s.name}{ep ? ` · ${ep}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: PLAY_BUTTON_GRADIENT }} onClick={() => launchCard(c, getToken)}>
                        <Play className="w-3.5 h-3.5" fill="white" />
                      </button>
                      {c.media_type === 'tv' && (
                        <button className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/15 text-neutral-400 hover:border-white/30 disabled:opacity-50 transition-all"
                          onClick={() => advance(c)} disabled={advancing === c.show_key} title="Mark watched">
                          {advancing === c.show_key
                            ? <div className="w-3 h-3 border-2 rounded-full animate-spin border-white/30 border-t-white" />
                            : <Check className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <div className="relative">
                        <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:text-neutral-300 hover:bg-white/5 transition-all"
                          onClick={() => setManaging(managing === c.show_key ? null : c.show_key)}>⋯</button>
                        {managing === c.show_key && (
                          <div className="absolute right-0 bottom-10 z-20 rounded-xl border border-white/10 bg-[#1a1a1a] shadow-xl overflow-hidden min-w-[160px]">
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5 flex items-center gap-2" onClick={() => setStatus(c, 'finished')}><Check className="w-4 h-4 text-green-400" /> Mark as finished</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => openPositioner(c)}>✎ Set episode…</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => { setManaging(null); setFixingService(c.show_key); }}>📡 Fix service…</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => openReminderForm(c)}>🔔 Set Reminder…</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => toggleDropAlert(c)}>📺 {dropAlerts.has(c.show_key) ? 'Remove Drop Alert' : 'Drop Alert'}</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5" onClick={() => removeFromHub(c)}>🗑 Remove from Hub</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {positioning?.show_key === c.show_key && (
                    <div className="mt-1 px-3 pb-2 rounded-2xl border border-white/8 bg-[#111]">
                      <EpisodePicker positioning={positioning} onChange={setPositioning} onSave={savePosition} onCancel={() => setPositioning(null)} />
                    </div>
                  )}
                  {fixingService === c.show_key && (
                    <ServicePicker onSave={(svcId) => fixService(c, svcId)} onCancel={() => setFixingService(null)} />
                  )}
                  {remindingCard === c.show_key && (
                    <ReminderForm title={reminderTitle} date={reminderDate} saving={reminderSaving}
                      onTitleChange={setReminderTitle} onDateChange={setReminderDate}
                      onSave={() => saveReminder(c)} onCancel={() => setRemindingCard(null)} />
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* STILL WATCHING? */}
      {staleCards.length > 0 && (
        <Section title="Still watching?">
          <div className="flex flex-col gap-2">
            {staleCards.map((c) => {
              const s = svc(c.service || c.service_id);
              const ep = epLabel(c.current_season, c.current_episode);
              return (
                <div key={c.show_key}>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-2xl border border-white/5 bg-[#111] opacity-75 w-full" style={{ minHeight: 140 }}>
                    <div className="relative flex-shrink-0 rounded-xl overflow-hidden" style={{ width: 88, height: 124 }}>
                      {c.image_url || c.poster_url
                        ? <img src={c.image_url || c.poster_url} alt={c.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg,${s.color}33,#0a0a0a)` }}><Tv className="w-4 h-4 text-neutral-600" /></div>}
                      <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-black" style={{ background: s.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate leading-tight" style={{ fontFamily: 'Outfit' }}>{c.title}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{s.name}{ep ? ` · ${ep}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: PLAY_BUTTON_GRADIENT }} onClick={() => launchCard(c, getToken)}>
                        <Play className="w-3.5 h-3.5" fill="white" />
                      </button>
                      <button className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/15 text-neutral-400 hover:border-white/30 disabled:opacity-50 transition-all"
                        onClick={() => advance(c)} disabled={advancing === c.show_key}>
                        {advancing === c.show_key
                          ? <div className="w-3 h-3 border-2 rounded-full animate-spin border-white/30 border-t-white" />
                          : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <div className="relative">
                        <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:text-neutral-300 hover:bg-white/5 transition-all"
                          onClick={() => setManaging(managing === c.show_key ? null : c.show_key)}>⋯</button>
                        {managing === c.show_key && (
                          <div className="absolute right-0 bottom-10 z-20 rounded-xl border border-white/10 bg-[#1a1a1a] shadow-xl overflow-hidden min-w-[160px]">
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5 flex items-center gap-2" onClick={() => setStatus(c, 'finished')}><Check className="w-4 h-4 text-green-400" /> Mark as finished</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => openPositioner(c)}>✎ Set episode…</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={() => { setManaging(null); setFixingService(c.show_key); }}>📡 Fix service…</button>
                            <button className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5" onClick={() => removeFromHub(c)}>🗑 Remove from Hub</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {positioning?.show_key === c.show_key && (
                    <div className="mt-1 px-3 pb-2 rounded-2xl border border-white/8 bg-[#111]">
                      <EpisodePicker positioning={positioning} onChange={setPositioning} onSave={savePosition} onCancel={() => setPositioning(null)} />
                    </div>
                  )}
                  {fixingService === c.show_key && (
                    <ServicePicker onSave={(svcId) => fixService(c, svcId)} onCancel={() => setFixingService(null)} />
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* WATCHLIST */}
      {hub?.watchlist && hub.watchlist.length > 0 && (
        <Section title="Your watchlist" href="/watchlist">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {hub.watchlist.slice(0, 6).map((c) => (
              <div key={c.show_key} className="flex items-center gap-3 p-4 rounded-2xl border border-white/5 bg-[#0A0A0A]">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: svc(c.service).color }} />
                <span className="text-sm font-semibold text-white truncate flex-1">{c.title}</span>
                <span className="text-xs text-neutral-600">{svc(c.service).name}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* TRENDING */}
      {trending.length > 0 && (
        <Section title="Trending This Week">
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {trending.map(item => (
              <Link key={item.tmdb_id} to={`/show/${item.tmdb_id}`} state={{ media_type: item.media_type || 'tv' }} className="flex-shrink-0 no-underline">
                <div className="w-48 rounded-xl overflow-hidden border border-white/5 hover:border-violet-500/30 transition-all" style={{ background: '#0A0A0A' }}>
                  {item.poster_url
                    ? <img src={item.poster_url} alt={item.title} className="w-full h-72 object-cover" />
                    : <div className="w-full h-72 bg-white/5 flex items-center justify-center"><Tv className="w-10 h-10 text-neutral-600" /></div>}
                  <div className="p-2">
                    <p className="text-xs font-semibold text-white truncate">{item.title}</p>
                    {item.rating > 0 && <p className="text-xs text-neutral-600">⭐ {item.rating}</p>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* BECAUSE YOU WATCHED */}
      {recommendations.map(rec => (
        <Section key={rec.tmdbId} title={`Because you're watching ${rec.forTitle}`}>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {rec.results.map(item => (
              <Link key={item.tmdb_id} to={`/show/${item.tmdb_id}`} state={{ media_type: item.media_type || 'tv' }} className="flex-shrink-0 no-underline">
                <div className="w-48 rounded-xl overflow-hidden border border-white/5 hover:border-violet-500/30 transition-all" style={{ background: '#0A0A0A' }}>
                  <img src={item.poster_url} alt={item.title} className="w-full h-72 object-cover" />
                  <div className="p-2">
                    <p className="text-xs font-semibold text-white truncate">{item.title}</p>
                    {item.rating > 0 && <p className="text-xs text-neutral-600">⭐ {item.rating}</p>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      ))}

      {/* MOODQ */}
      <Section title="🎭 MoodQ" subtitle="Pick a mood, get a show">
        <div className="grid grid-cols-5 gap-2 mb-3">
          {HUB_MOODS.map((mood) => (
            <button
              key={mood.id}
              onClick={() => fetchMood(mood.id)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${
                selectedMood === mood.id
                  ? 'bg-violet-600/20 border-violet-500/50 text-white'
                  : 'bg-[#0A0A0A] border-white/5 hover:border-white/15 text-neutral-300'
              }`}
            >
              <span className="text-[2.5rem] leading-none">{mood.emoji}</span>
              <span className="text-xs font-semibold">{mood.label}</span>
            </button>
          ))}
        </div>
        {moodLoading && (
          <div className="flex items-center justify-center py-6">
            <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!moodLoading && currentMoodResult && (
          <div className="flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-[#0A0A0A] hub-rise">
            {currentMoodResult.poster_url
              ? <img src={currentMoodResult.poster_url} alt={currentMoodResult.title} className="w-12 h-16 rounded-lg object-cover flex-shrink-0" />
              : <div className="w-12 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0"><Tv className="w-5 h-5 text-neutral-600" /></div>}
            <div className="flex-1 min-w-0">
              {currentMoodResult.reason && <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide truncate">{currentMoodResult.reason}</p>}
              <p className="text-sm font-bold text-white truncate">{currentMoodResult.title}</p>
              {currentMoodResult.rating > 0 && <p className="text-xs text-neutral-500">⭐ {currentMoodResult.rating}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button className="px-3 py-1.5 rounded-full text-xs font-bold text-white flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}
                onClick={() => launchCard(currentMoodResult, getToken)}>
                <Play className="w-3 h-3" fill="white" /> Play
              </button>
              {moodResults.length > 1 && (
                <button className="px-3 py-1.5 rounded-full text-xs font-semibold text-neutral-300 border border-white/10 hover:border-white/20"
                  onClick={() => setMoodResultIndex(i => (i + 1) % moodResults.length)}>
                  Next →
                </button>
              )}
            </div>
          </div>
        )}
        {!moodLoading && selectedMood && moodResults.length === 0 && (
          <p className="text-xs text-neutral-600 text-center py-4">
            No suggestions found. Try connecting more services on{' '}
            <Link to="/services" className="text-violet-400 hover:underline">Services</Link>.
          </p>
        )}
      </Section>

      {/* QMATES */}
      <Section title="QMates">
        {qmates.length === 0 ? (
          <div className="flex items-center justify-between p-4 rounded-2xl border border-white/5 bg-[#0A0A0A]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center">
                <Users className="w-4 h-4 text-neutral-600" />
              </div>
              <p className="text-sm text-neutral-500">Watch together with friends</p>
            </div>
            <Link to="/qmate" className="text-xs font-bold px-3 py-1.5 rounded-full border border-white/10 hover:border-white/20 text-neutral-300">Invite</Link>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 rounded-2xl border border-white/5 bg-[#0A0A0A]">
            <div className="flex -space-x-2">
              {qmates.slice(0, 3).map((q, i) => (
                <div key={q.qmate_id || i}
                     className="w-9 h-9 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center text-xs font-bold text-white"
                     style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)', zIndex: 3 - i }}>
                  {q.friend_name?.[0]?.toUpperCase() || '?'}
                </div>
              ))}
            </div>
            <p className="text-sm text-neutral-300 flex-1">
              {qmates.slice(0, 3).map(q => q.friend_name?.split(' ')[0]).filter(Boolean).join(', ')}
              {qmates.length > 3 ? ` +${qmates.length - 3} more` : ''}
            </p>
            <Link to="/qmate" className="text-xs font-bold px-3 py-1.5 rounded-full border border-white/10 hover:border-white/20 text-neutral-300">View All</Link>
          </div>
        )}
      </Section>

      {/* WATCHED */}
      {hub?.finished && hub.finished.length > 0 && (
        <Section title={`Watched · ${hub.counts?.finished ?? hub.finished.length}`}>
          <div className="flex flex-col gap-2">
            {hub.finished.map((c) => {
              const s = svc(c.service || c.service_id);
              return (
                <div key={c.show_key} className="flex items-center gap-3 px-3 py-2 rounded-2xl border border-white/5 bg-[#111] opacity-70 w-full" style={{ minHeight: 72 }}>
                  <div className="relative flex-shrink-0 rounded-xl overflow-hidden" style={{ width: 44, height: 62 }}>
                    {c.image_url || c.poster_url
                      ? <img src={c.image_url || c.poster_url} alt={c.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg,${s.color}33,#0a0a0a)` }}><Tv className="w-4 h-4 text-neutral-600" /></div>}
                    <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-black" style={{ background: s.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate leading-tight" style={{ fontFamily: 'Outfit' }}>{c.title}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{s.name}{c.current_season ? ` · S${c.current_season}` : ''}</p>
                  </div>
                  <button
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-neutral-400 border border-white/10 hover:border-white/25 hover:text-neutral-200 transition-all flex-shrink-0"
                    onClick={async () => {
                      try {
                        const token = await getToken();
                        await axios.post(`${API}/track/set`, { show_key: c.show_key, status: 'watching' }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success(`${c.title} moved back to watching`);
                        await load();
                      } catch { toast.error('Could not update'); }
                    }}>↩ Rewatch</button>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}

const SERVICE_PICKER_OPTIONS = [
  { id: 'netflix',        name: 'Netflix' },
  { id: 'hulu',           name: 'Hulu' },
  { id: 'hbo_max',        name: 'Max' },
  { id: 'disney_plus',    name: 'Disney+' },
  { id: 'amazon_prime',   name: 'Prime Video' },
  { id: 'apple_tv',       name: 'Apple TV+' },
  { id: 'peacock',        name: 'Peacock' },
  { id: 'paramount_plus', name: 'Paramount+' },
];

function ServicePicker({ onSave, onCancel }) {
  return (
    <div className="mt-1 px-3 pt-2 pb-3 rounded-2xl border border-white/8 bg-[#111]">
      <p className="text-[10px] font-black tracking-widest text-neutral-500 uppercase mb-2">Fix service</p>
      <div className="grid grid-cols-2 gap-1.5">
        {SERVICE_PICKER_OPTIONS.map(s => (
          <button key={s.id}
            className="py-1.5 px-2 rounded-lg text-xs font-semibold text-left transition-all hover:opacity-90"
            style={{ background: `${SERVICE_META[s.id]?.color}22`, border: `1px solid ${SERVICE_META[s.id]?.color}44`, color: SERVICE_META[s.id]?.color }}
            onClick={() => onSave(s.id)}>
            {s.name}
          </button>
        ))}
      </div>
      <button onClick={onCancel} className="mt-2 w-full py-1.5 rounded-lg text-xs text-neutral-500 hover:text-neutral-300 hover:bg-white/5 transition-all">Cancel</button>
    </div>
  );
}

function ReminderForm({ title, date, saving, onTitleChange, onDateChange, onSave, onCancel }) {
  return (
    <div className="mt-2 p-4 rounded-2xl border border-violet-500/20 bg-[#0A0A0A]">
      <p className="text-xs font-bold text-violet-400 mb-3 tracking-[0.06em] uppercase">Set Reminder</p>
      <div className="flex flex-col gap-2 mb-3">
        <input className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-violet-500/50"
          placeholder="Reminder title" value={title} onChange={e => onTitleChange(e.target.value)} />
        <input type="date" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-violet-500/50"
          value={date} onChange={e => onDateChange(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <button className="px-4 py-2 rounded-full text-sm font-bold text-white disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }} onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="px-4 py-2 rounded-full text-sm text-neutral-400 border border-white/10 hover:border-white/20" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ResumeCard({ card, episodeBadge, onPlay, advancing, onAdvance, menuOpen, onMenuToggle, onFinished, onRemove,
                      positioningState, onSetEpisode, onPositionChange, onPositionSave, onPositionCancel,
                      onSetReminder, onDropAlert, dropAlertActive, onFlagCorrect, onFlagWrong, correcting, onFixService,
                      onUndoInference }) {
  const { settings } = useA11y();
  const service = svc(card.service || card.service_id);
  const nextEp = (card.next_season != null || card.next_episode != null) ? epLabel(card.next_season, card.next_episode) : null;
  const curEp = epLabel(card.current_season, card.current_episode);

  return (
    <div className="hub-rise w-full mb-2">
      <div className="flex items-stretch gap-3 rounded-2xl border border-white/8 bg-[#111]" style={{ minHeight: 320 }}>
        <div className="relative flex-shrink-0 rounded-l-2xl overflow-hidden" style={{ width: 220 }}>
          {card.poster_url || card.image_url
            ? <img src={card.poster_url || card.image_url?.replace('/w300/', '/w500/')} alt={card.title} className="w-full h-full object-cover object-top" />
            : <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${service.color}33, #0a0a0a)` }} />}
          <div className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 rounded-full border-2 border-black" style={{ background: service.color }} />
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0 py-3 pr-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0"
                   style={{ background: `${service.color}28`, border: `1px solid ${service.color}50`, color: service.color }}>
                {service.name}
              </div>
              {card.inferred && (
                <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0"
                     style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#a78bfa' }}>
                  ✦ Inferred
                </div>
              )}
              {card.last_watched_at && <span className="text-[10px] text-white/40 truncate">{timeAgo(card.last_watched_at)}</span>}
            </div>
            <div className="relative flex-shrink-0">
              <button className="w-7 h-7 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all" onClick={onMenuToggle}>⋯</button>
              {menuOpen && (
                <div className="absolute right-0 top-8 z-20 rounded-xl border border-white/10 bg-[#1a1a1a] shadow-xl overflow-hidden min-w-[170px]">
                  <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5 flex items-center gap-2" onClick={onFinished}><Check className="w-4 h-4 text-green-400" /> Mark as finished</button>
                  <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={onSetEpisode}>✎ Set episode…</button>
                  <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={onFixService}>📡 Fix service…</button>
                  <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={onSetReminder}>🔔 Set Reminder…</button>
                  <button className="w-full text-left px-4 py-3 text-sm text-neutral-200 hover:bg-white/5" onClick={onDropAlert}>📺 {dropAlertActive ? 'Remove Drop Alert' : 'Drop Alert'}</button>
                  <button className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5" onClick={onRemove}>🗑 Remove from Hub</button>
                  {card.inferred && (
                    <button className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5 flex items-center gap-2" onClick={onUndoInference}>
                      ↩ Undo — wasn't watching this
                    </button>
                  )}
                  {card.confidence != null && (
                    <>
                      <div className="h-px bg-white/5 mx-4" />
                      <p className="px-4 py-2 text-xs text-neutral-600">Auto-detected · correct?</p>
                      <div className="flex gap-2 px-4 pb-3">
                        <button onClick={onFlagCorrect} disabled={correcting} className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-50">👍 Yes</button>
                        <button onClick={onFlagWrong} disabled={correcting} className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50">👎 Wrong show</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mt-1">
            {card.tonight_reason && !settings?.spoilerShield && (
              <p className="text-[10px] font-semibold mb-0.5" style={{ color: '#a78bfa' }}>✦ {card.tonight_reason}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-black text-white leading-tight truncate" style={{ fontFamily: 'Outfit' }}>{card.title}</h2>
              {episodeBadge && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                  episodeBadge.days_until === 0 ? 'bg-red-500/20 text-red-400' :
                  episodeBadge.days_until > 0 ? 'bg-violet-500/20 text-violet-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {episodeBadge.days_until === 0 ? '🔴 ' : episodeBadge.days_until > 0 ? '🗓 ' : '✨ '}
                  {episodeBadge.label}
                </span>
              )}
            </div>
            {card.media_type === 'tv' && (nextEp || curEp) && (
              <p className="text-xs text-white/50 mt-0.5">
                {nextEp ? <>Next: <button onClick={onSetEpisode} className="text-white/80 font-semibold">{nextEp}</button></> : <>On: <button onClick={onSetEpisode} className="text-white/80 font-semibold">{curEp}</button></>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button className="inline-flex items-center gap-1.5 font-bold text-white transition-all hover:opacity-90 active:scale-95 text-xs"
              style={{ background: PLAY_BUTTON_GRADIENT, borderRadius: 20, padding: '8px 16px', border: 'none', cursor: 'pointer' }} onClick={onPlay}>
              <Play className="w-3.5 h-3.5" fill="white" />
              {nextEp ? `Play ${nextEp}` : 'Resume'}
            </button>
            {card.media_type === 'tv' && (
              <button className="inline-flex items-center gap-1.5 font-semibold transition-all hover:opacity-90 disabled:opacity-50 active:scale-95 text-xs"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '8px 14px', color: '#fff', cursor: 'pointer' }}
                onClick={onAdvance} disabled={advancing}>
                {advancing ? <div className="w-3.5 h-3.5 border-2 rounded-full animate-spin border-white/40 border-t-white" /> : <Check className="w-3.5 h-3.5" />}
                Watched it
              </button>
            )}
          </div>
        </div>
      </div>
      {positioningState && (
        <div className="mt-2">
          <EpisodePicker positioning={positioningState} onChange={onPositionChange} onSave={onPositionSave} onCancel={onPositionCancel} />
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, href, children }) {
  return (
    <div className="mt-4 hub-rise">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-[11px] font-black tracking-widest uppercase text-neutral-500">{title}</h3>
          {subtitle && <p className="text-xs text-neutral-600 mt-0.5">{subtitle}</p>}
        </div>
        {href && (
          <a href={href} className="flex items-center gap-0.5 text-xs text-neutral-500 hover:text-neutral-300">
            See all <ChevronRight className="w-3 h-3" />
          </a>
        )}
      </div>
      {children}
    </div>
  );
}
